import { Card, Collapse, Flex, Tag } from 'antd';
import { Link, Text } from 'components/mvp-typography';
import { ClinicianQueryResponseReference } from 'lib/api/firebase/apis/clinician-query';
import { grey } from 'styles/colors';
import { Message } from '../types';
import { ChatMessage } from './message';

export interface QuestionResponseGroup {
  id: string;
  /**
   * The question that was asked
   */
  question: { message: Message };
  /**
   * The generated answer. This is undefined if the question has not finished processing, or if the question processing failed
   */
  response?: {
    message: Message;
    /**
     * The documents that were selected by the backend to be used in the LLM response generation
     */
    references: ClinicianQueryResponseReference[];
  };
}
/**
 * Displays a question and a response
 *
 * @param props
 * @returns
 */
export function QuestionResponseSet(props: {
  responseGroup: QuestionResponseGroup;
}) {
  const { response, question } = props.responseGroup;
  return (
    <div
      style={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 4 * 12
      }}
    >
      <div>
        <p
          style={{ fontWeight: 'bold', lineHeight: '1rem', marginBottom: 0 }}
        ></p>
        <ChatMessage content={question.message.content} />
      </div>
      {
        /**
         * The response is ready to display
         */
        response && (
          <div>
            <Text
              style={{
                fontWeight: 'bold',
                lineHeight: '1rem'
              }}
            >
              ConcussionRx Companion
            </Text>
            <ChatMessage
              content={response.message.content ?? ''}
              noBackground
              color={'#000'}
            />
            <div>
              <Collapse
                style={{
                  background: 'none'
                }}
                defaultActiveKey={'results'}
              >
                <Collapse.Panel key={'results'} header={<Text>Results</Text>}>
                  {response.references.map((reference, index) => (
                    <ReferenceResult
                      key={`reference-${reference.id}`}
                      reference={reference}
                      index={index}
                    />
                  ))}
                </Collapse.Panel>
              </Collapse>
            </div>
          </div>
        )
      }
    </div>
  );
}

interface ReferenceResultProps {
  reference: ClinicianQueryResponseReference;
  index: number;
}

function ReferenceResult(props: ReferenceResultProps) {
  const pubmedLink = `https://pubmed.ncbi.nlm.nih.gov/${props.reference.id}/`;

  return (
    <>
      <Flex vertical gap={'small'}>
        <Text>
          {props.index + 1}. {props.reference.title}
        </Text>
        <Card
          style={{
            borderRadius: '10px',
            background: grey['light']
          }}
        >
          <Link href={pubmedLink}>{pubmedLink}</Link>
        </Card>
        <div
          style={{
            marginTop: '0.5rem',
            marginBottom: '0.5rem'
          }}
        >
          {props.reference.publicationTypes.map((publicationType, index) => (
            <ReferenceTag
              key={`referencetag-${index}`}
              publicationType={publicationType}
            />
          ))}
        </div>
      </Flex>
      <Collapse
        bordered={false}
        style={{
          background: 'none',
          fontSize: '12px !important'
        }}
      >
        <Collapse.Panel key={props.reference.id} header="Show abstract">
          {props.reference.abstract.length > 0 ? (
            <Text color={'#767679'} style={{ whiteSpace: 'preserve' }}>
              {props.reference.abstract}
            </Text>
          ) : (
            <Text color={'#767679'} style={{ whiteSpace: 'preserve' }}>
              No abstract was available. Please use the{' '}
              <Link href={pubmedLink}>direct link</Link> to view the article.
            </Text>
          )}
        </Collapse.Panel>
      </Collapse>
    </>
  );
}

const ReferenceTag = (props: { publicationType: string }) => (
  <Tag
    style={{
      borderRadius: '100px',
      background: '#C6ABFF4D',
      borderColor: '#B277FF80',
      color: '#230056B2'
    }}
  >
    {props.publicationType}
  </Tag>
);
